/**
 * The sidebar's navigation items.
 *
 * The list itself lives in `shared/src/config/nav.config.ts`, alongside the
 * profile menu — web and mobile each kept their own copy for a while and drifted
 * badly, so order, labels, grouping and role gates are one definition now. What
 * remains here is the adapter: manifest entries in, the `SidebarItem` shape the
 * web sidebar renders out.
 */

import {
  resolveNav,
  resolveTabBar,
  SIDEBAR_NAV,
} from '@personal-trainer-app/shared/src/config/nav.config';

import type {
  BuildSidebarItemsProps,
  BuildTabBarItemsProps,
} from './authenticatedNav.types';
import type { TabBarItemData } from '@/layouts/components/BottomTabBar';
import type { SidebarItem } from '@/layouts/components/Sidebar';

import { navIcon } from '@/layouts/components/navIcons';

/**
 * Build the sidebar for a role.
 *
 * Role gating is the manifest's — a trainer gets Clients, a client does not,
 * and an unresolved role sees neither rather than flashing a destination it is
 * about to be refused.
 */
export function buildSidebarItems({
  role,
}: BuildSidebarItemsProps): SidebarItem[] {
  return resolveNav(SIDEBAR_NAV, { role, platform: 'web' }).map((entry) =>
    entry.kind === 'divider'
      ? {
          type: 'divider' as const,
          id: entry.id,
          ...(entry.label ? { label: entry.label } : {}),
        }
      : {
          id: entry.id,
          label: entry.label,
          icon: navIcon(entry.icon, 'h-5 w-5'),
          href: entry.href,
        }
  );
}

/**
 * Build the phone tab bar for the viewer's saved dashboard profile.
 *
 * Home and Me are fixed; the two tabs either side of Start Workout come from
 * the manifest's `TAB_BAR_SLOTS`, the same list the mobile app's bar reads.
 */
export function buildTabBarItems({
  profile,
  role,
}: BuildTabBarItemsProps): TabBarItemData[] {
  return resolveTabBar(profile, role).map((item) => ({
    id: item.id,
    label: item.label,
    icon: navIcon(item.icon, 'h-6 w-6'),
    href: item.href,
  }));
}
